import React, { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertCircle, Check, X as XIcon, Upload, Stethoscope, User } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import axios from "axios";
import { toast } from "sonner";
import { trackSignUp, trackTrialStarted, trackPlanPurchased } from "@/utils/analytics";
import { readGuestMeal, autoLogGuestMealIfAny, clearGuestMeal } from "@/lib/guestMealStorage";
import { UtensilsCrossed } from "lucide-react";
import CycleOnboardingModal, { shouldShowCycleOnboarding } from "@/components/CycleOnboardingModal";
import RegisterProgressStepper from "@/components/RegisterProgressStepper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PasswordChecks({ password, bg }) {
  const checks = [
    { ok: password.length >= 8, label: bg ? "Минимум 8 символа" : "At least 8 characters" },
    { ok: /[A-Z]/.test(password), label: bg ? "Една главна буква" : "One uppercase letter" },
    { ok: /[a-z]/.test(password), label: bg ? "Една малка буква" : "One lowercase letter" },
    { ok: /[0-9]/.test(password), label: bg ? "Една цифра" : "One digit" },
  ];
  if (password.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-1 mt-1.5">
      {checks.map((c) => (
        <div key={c.label} className={`flex items-center gap-1 text-[11px] ${c.ok ? "text-green-600" : "text-gray-400"}`}>
          {c.ok ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
          {c.label}
        </div>
      ))}
    </div>
  );
}

function isPwdValid(password) {
  return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
}

function PatientRegisterForm({ bg }) {
  const { register, verifyEmail, refreshUser, API } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const verifyEmail_param = searchParams.get("verify");
  const refParam = searchParams.get("ref") || "";
  // Intent passed from SymptomAssessment paywall — if set we skip the legacy
  // 6-step onboarding wizard and route directly according to the paywall CTA.
  const assessmentIntent = location.state?.intent || null;
  const assessmentId = location.state?.assessmentId || null;
  const [name, setName] = useState("");
  const [email, setEmail] = useState(verifyEmail_param || "");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(refParam.toUpperCase());
  const [refValid, setRefValid] = useState(null); // null | { valid, referrer_name, referee_reward }
  // iter1118 — отделно поле за „Код за достъп на пациент" (patient_gift).
  const [promoCode, setPromoCode] = useState("");
  const [promoValid, setPromoValid] = useState(null); // null | { valid, discount_type, ... }
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(verifyEmail_param ? "verify" : "form");
  const [code, setCode] = useState("");
  // iter304 — GDPR Art. 9 consent + Art. 8 age verification
  const [consentHealth, setConsentHealth] = useState(false);
  const [consentAge, setConsentAge] = useState(false);
  // iter309 — optional DOB; stronger Art. 8 evidence than a self-declared checkbox.
  const [dateOfBirth, setDateOfBirth] = useState("");
  // iter1091 — Guest trial meal, ако landing widget-a е логнал такова.
  const [guestMeal] = useState(() => readGuestMeal());
  // iter1095 — Cycle onboarding wizard state (женски users post-verify).
  const [cycleOnbOpen, setCycleOnbOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  // iter1096 — Track дали user-a e жена (за progress stepper skip-cycle логика).
  // Няма как да знаем преди verify → показваме и трите стъпки, а skip=true
  // само след като модалът се появи (или е dismiss-нат).
  const [skipCycleInStepper, setSkipCycleInStepper] = useState(false);

  React.useEffect(() => {
    const t = setTimeout(async () => {
      if (!referralCode || referralCode.length < 5) { setRefValid(null); return; }
      try {
        const { data } = await axios.post(`${API}/referral/validate`, { code: referralCode });        setRefValid(data);
      } catch { setRefValid(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode, API]);

  // iter1118 — валидация на „Код за достъп" (patient_gift).
  React.useEffect(() => {
    const t = setTimeout(async () => {
      if (!promoCode || promoCode.length < 5) { setPromoValid(null); return; }
      try {
        const { data } = await axios.get(
          `${API}/promo/validate`,
          { params: { code: promoCode, context: "signup" } },
        );
        setPromoValid(data);
      } catch { setPromoValid(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [promoCode, API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPwdValid(password)) {
      setError(bg ? "Паролата не отговаря на изискванията" : "Password does not meet requirements");
      return;
    }
    // iter304 — explicit GDPR Art. 9 consent is mandatory.
    if (!consentHealth || !consentAge) {
      setError(bg
        ? "Моля потвърдете и двете съгласия преди да продължите."
        : "Please confirm both consents before continuing.");
      return;
    }
    setLoading(true);
    try {
      const result = await register(
        email, password, name,
        refValid?.valid ? referralCode.toUpperCase() : undefined,
        {
          health_data_consent: consentHealth,
          age_confirmed_16_plus: consentAge,
          terms_version_accepted: "v1.0-2026-02",
          privacy_version_accepted: "v1.0-2026-02",
          date_of_birth: dateOfBirth || undefined,
          promo_code: promoValid?.valid ? promoCode.toUpperCase() : undefined,
        },
      );
      if (result?.status === "verification_required") {
        setStep("verify");
        toast.success(bg ? "Код за потвърждение е изпратен на имейла ви" : "Verification code sent to your email");
        // GA4 conversion: user requested account creation (before verify).
        trackSignUp("email");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    if (code.length !== 6) {
      setError(bg ? "Кодът трябва да е 6 цифри" : "Code must be 6 digits");
      return;
    }
    setLoading(true);
    try {
      await verifyEmail(email, code);
      toast.success(bg ? "Имейлът е потвърден!" : "Email verified!");

      // Link any anonymous assessment completed before register to this user.
      // NOTE: /assessment/claim no longer marks onboarding complete — the
      // real Health Assessment at /onboarding is the mandatory next step.
      if (assessmentId) {
        try { await axios.post(`${API}/assessment/claim`, { assessment_id: assessmentId }); } catch {}
      }
      try { await refreshUser(); } catch {}

      // iter1091 — Guest Meal Save: auto-log храненето от landing widget-a
      // директно в diary-то. Ако успее → toast + маркер в URL за welcome.
      let welcomeMeal = "";
      try {
        const token = sessionStorage.getItem("nutramilo_access_token") || localStorage.getItem("nutramilo_access_token");
        if (token && readGuestMeal()) {
          const res = await autoLogGuestMealIfAny(API, token);
          if (res.logged) {
            welcomeMeal = "&meal_logged=1";
            toast.success(bg
              ? "Пробният ти анализ е добавен в дневника! 🍽️"
              : "Your trial meal was saved to your diary! 🍽️");
          } else {
            clearGuestMeal();
          }
        }
      } catch { clearGuestMeal(); }

      // Route based on paywall intent (if any) — Wise checkout е в Pricing modal.
      if (assessmentIntent === "core") {
        try {
          if (assessmentId) await axios.post(`${API}/trial/start`, { assessment_id: assessmentId });
          trackTrialStarted("core", 7);
        } catch {}
        navigate("/pricing?intent=core");
        return;
      } else if (assessmentIntent === "micro_unlock") {
        navigate("/pricing?intent=micro_unlock");
        return;
      } else if (assessmentIntent === "free" && assessmentId) {
        try { await axios.post(`${API}/trial/start`, { assessment_id: assessmentId }); } catch {}
        trackTrialStarted("free", 7);
      }

      // All new users go through the 6-step Health Assessment first. The
      // ProtectedRoute also enforces this, but explicit nav is clearer.
      // iter1091 — Ако логнахме guest meal, добавяме query param за welcome
      // banner в Onboarding page-a (може да показва „твоето първо хранене е в дневника").
      const nextRoute = `/onboarding${welcomeMeal ? `?meal_logged=1` : ""}`;

      // iter1095 — За жени без LMP → показваме Cycle Onboarding wizard-а тук
      // (post-verify) вместо на първо Dashboard влизане. Далеч по-плътен
      // conversion защото user-a е още „в потока".
      try {
        const token = sessionStorage.getItem("nutramilo_access_token") || localStorage.getItem("nutramilo_access_token");
        const me = await axios.get(`${API}/auth/me`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (shouldShowCycleOnboarding(me.data)) {
          setPendingNav(nextRoute);
          setCycleOnbOpen(true);
          setLoading(false);
          return;
        }
        // iter1096 — мъже или жени с вече попълнено LMP → маркираме
        // стъпка 2 като „прескочена" във stepper-a.
        setSkipCycleInStepper(true);
      } catch { /* if auth/me fails just navigate directly */ }

      navigate(nextRoute);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/auth/resend-code`, { email });
      toast.success(bg ? "Нов код е изпратен" : "New code sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (step === "verify") {
    return (
      <form onSubmit={handleVerify} className="space-y-4" data-testid="verify-form">
        {/* iter1096 — Progress stepper: сме на стъпка 1 (Регистрация) докато verify не мине. */}
        <RegisterProgressStepper currentStep={1} skipCycle={skipCycleInStepper} />
        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center">
            <Check className="w-7 h-7 text-white" />
          </div>
          <p className="text-sm text-[#334155] font-medium">{bg ? "Проверете имейла си" : "Check your email"}</p>
          <p className="text-xs text-[#94A3B8] mt-1">{bg ? `Изпратихме 6-цифрен код на ${email}` : `We sent a 6-digit code to ${email}`}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-[#1A1A2E] text-sm font-medium">{bg ? "Код за потвърждение" : "Verification code"}</Label>
          <Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-14 text-center text-2xl font-bold tracking-[0.5em] bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
            data-testid="verification-code-input" autoFocus />
        </div>
        <Button type="submit" disabled={loading || code.length !== 6} data-testid="verify-submit-btn"
          className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
          {loading ? (bg ? "Проверка..." : "Verifying...") : (bg ? "Потвърди" : "Verify")}
        </Button>
        <button type="button" onClick={handleResend} className="w-full text-xs text-[#0EA5E9] hover:underline" data-testid="resend-code-btn">
          {bg ? "Изпрати нов код" : "Resend code"}
        </button>
        {/* iter1095 — Cycle Onboarding wizard (post-verify) — женски users без LMP.
            И submit, и skip водят до pendingNav (Onboarding), а не към twin-network
            (default-a на модала), защото user-a още не е попълнил Health Assessment. */}
        <CycleOnboardingModal
          open={cycleOnbOpen}
          onOpenChange={(v) => {
            setCycleOnbOpen(v);
            if (!v && pendingNav) {
              const target = pendingNav;
              setPendingNav(null);
              setTimeout(() => navigate(target), 100);
            }
          }}
          onComplete={() => { /* no-op — pendingNav handles routing */ }}
          stepperSlot={<RegisterProgressStepper currentStep={2} variant="compact" />}
        />
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* iter1096 — Progress stepper: 1/3 Регистрация активна. */}
      <RegisterProgressStepper currentStep={1} />
      {/* iter1091 — Guest Meal detected banner (Landing widget → Register) */}
      {guestMeal && guestMeal.analysis?.food_name && (
        <div
          className="flex items-start gap-2.5 p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 text-emerald-800 text-sm"
          data-testid="register-guest-meal-banner"
        >
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
            <UtensilsCrossed className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[13px] leading-tight">
              {bg
                ? "Ще пренесем пробния ти анализ в дневника"
                : "We'll save your trial meal to your diary"}
            </div>
            <div className="text-xs mt-0.5 opacity-80 truncate">
              🍽️ {guestMeal.analysis.food_name}
              {typeof guestMeal.analysis.calories === "number" && ` — ${Math.round(guestMeal.analysis.calories)} kcal`}
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="register-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-[#1A1A2E] text-sm font-medium">{bg ? "Име" : "Name"}</Label>
        <Input id="name" type="text" placeholder={bg ? "Вашето име" : "Your name"} value={name} onChange={(e) => setName(e.target.value)} required
          data-testid="register-name-input" className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email" className="text-[#1A1A2E] text-sm font-medium">{bg ? "Имейл" : "Email"}</Label>
        <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required
          data-testid="register-email-input" className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-[#1A1A2E] text-sm font-medium">{bg ? "Парола" : "Password"}</Label>
        <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
          data-testid="register-password-input" className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
        <PasswordChecks password={password} bg={bg} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="referral_code" className="text-[#1A1A2E] text-sm font-medium flex items-center gap-2">
          {bg ? "Код за препоръка (незадължително)" : "Referral code (optional)"}
        </Label>
        <Input
          id="referral_code" type="text"
          placeholder={bg ? "NM-ABC123" : "NM-ABC123"}
          value={referralCode}
          onChange={(e) => setReferralCode(e.target.value.toUpperCase().slice(0, 12))}
          data-testid="register-referral-input"
          className={`h-10 text-sm bg-[#F8FAFC] border-[#E2E8F0] rounded-xl ${refValid?.valid ? 'border-green-400 focus:ring-green-400' : ''}`}
        />
        {refValid?.valid && (
          <p className="text-[11px] text-green-600" data-testid="register-referral-ok">
            {bg
              ? `✓ Код на ${refValid.referrer_name || "приятел"} — ще получите +${refValid.referee_reward} кредита след като закупите план или кредити`
              : `✓ Code from ${refValid.referrer_name || "a friend"} — you'll earn +${refValid.referee_reward} credits after you purchase a plan or credits`}
          </p>
        )}
        {referralCode.length >= 5 && refValid && refValid.valid === false && (
          <p className="text-[11px] text-amber-600">
            {bg ? "Невалиден код" : "Invalid code"}
          </p>
        )}
      </div>

      {/* iter1118 — отделно поле за „Код за достъп" (patient_gift). */}
      <div className="space-y-1.5">
        <Label htmlFor="promo_code" className="text-[#1A1A2E] text-sm font-medium flex items-center gap-2">
          {bg ? "Код за достъп от лекар (незадължително)" : "Access code from doctor (optional)"}
        </Label>
        <Input
          id="promo_code" type="text"
          placeholder="PAT-XXXXXX"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value.toUpperCase().slice(0, 14))}
          data-testid="register-promo-input"
          className={`h-10 text-sm bg-[#F8FAFC] border-[#E2E8F0] rounded-xl ${promoValid?.valid ? 'border-green-400 focus:ring-green-400' : ''}`}
        />
        {promoValid?.valid && (
          <p className="text-[11px] text-green-600" data-testid="register-promo-ok">
            {bg
              ? "✓ Валиден код — ще получите пълен достъп след потвърждение на имейла"
              : "✓ Valid code — full access after email confirmation"}
          </p>
        )}
        {promoCode.length >= 5 && promoValid && promoValid.valid === false && (
          <p className="text-[11px] text-amber-600">
            {bg ? "Невалиден или изчерпан код" : "Invalid or exhausted code"}
          </p>
        )}
      </div>
      {/* iter304 — GDPR Art. 9 explicit health-data consent + Art. 8 age */}
      <div className="space-y-2 pt-2 border-t border-gray-200" data-testid="register-consent-block">
        <label className="flex items-start gap-2 text-[12px] leading-snug cursor-pointer text-gray-700">
          <input
            type="checkbox"
            checked={consentHealth}
            onChange={(e) => setConsentHealth(e.target.checked)}
            className="mt-0.5 accent-[#0EA5E9] shrink-0"
            data-testid="register-consent-health"
          />
          <span>
            {bg
              ? <>Съгласен съм Nutramilo да обработва моите здравни данни за изчисления, AI анализи и препоръки (GDPR Чл. 9). Прочетох <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] underline">Политиката за поверителност</a>.</>
              : <>I consent to Nutramilo processing my health data for calculations, AI analysis and recommendations (GDPR Art. 9). I have read the <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] underline">Privacy Policy</a>.</>}
          </span>
        </label>
        <label className="flex items-start gap-2 text-[12px] leading-snug cursor-pointer text-gray-700">
          <input
            type="checkbox"
            checked={consentAge}
            onChange={(e) => setConsentAge(e.target.checked)}
            className="mt-0.5 accent-[#0EA5E9] shrink-0"
            data-testid="register-consent-age"
          />
          <span>
            {bg
              ? "Потвърждавам, че съм поне 16 години (GDPR Чл. 8)."
              : "I confirm I am at least 16 years old (GDPR Art. 8)."}
          </span>
        </label>
        {/* iter309 — Optional DOB for stronger GDPR Art. 8 audit trail. */}
        <div className="pt-1 pl-5">
          <label className="text-[11px] text-gray-500 font-medium block mb-1">
            {bg
              ? "По желание: дата на раждане (по-силно доказателство за възраст)"
              : "Optional: date of birth (stronger age evidence)"}
          </label>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            max={new Date().toISOString().split("T")[0]}
            className="w-full h-9 px-2 text-[12px] border border-gray-200 rounded-md focus:border-[#0EA5E9] focus:outline-none bg-white/70"
            data-testid="register-dob-input"
            aria-label={bg ? "Дата на раждане" : "Date of birth"}
          />
        </div>
      </div>
      <Button type="submit" disabled={loading} data-testid="register-submit-btn"
        className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
        {loading ? (bg ? "Създаване..." : "Creating account...") : (bg ? "Създай акаунт" : "Create account")}
      </Button>
    </form>
  );
}

function ClinicianRegisterForm({ bg }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", specialty: "", institution: "", orcid: "", linkedin: "", institutional_email: "" });
  const [cvFile, setCvFile] = useState(null);
  const [diplomaFile, setDiplomaFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "verify"
  const [code, setCode] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPwdValid(form.password)) {
      setError(bg ? "Паролата не отговаря на изискванията" : "Password does not meet requirements");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(bg ? "Паролите не съвпадат" : "Passwords do not match");
      return;
    }
    if (!form.specialty.trim()) {
      setError(bg ? "Специалността е задължителна" : "Specialty is required");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("email", form.email);
      fd.append("password", form.password);
      fd.append("name", form.name);
      fd.append("specialty", form.specialty);
      fd.append("institution", form.institution);
      fd.append("orcid", form.orcid);
      fd.append("linkedin", form.linkedin);
      fd.append("institutional_email", form.institutional_email);
      if (cvFile) fd.append("cv_file", cvFile);
      if (diplomaFile) fd.append("diploma_file", diplomaFile);

      const { data } = await axios.post(`${API}/auth/register-clinician`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.status === "verification_required") {
        setStep("verify");
        toast.success(bg ? "Код за потвърждение е изпратен на имейла ви" : "Verification code sent");
      } else {
        setSuccess(true);
        toast.success(bg ? "Заявката е изпратена!" : "Application submitted!");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    if (code.length !== 6) { setError(bg ? "Кодът трябва да е 6 цифри" : "Code must be 6 digits"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-clinician-email`, { email: form.email, code });
      setSuccess(true);
      toast.success(bg ? "Имейлът е потвърден! Заявката ви ще бъде прегледана от администратор." : "Email verified! Your application will be reviewed.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/auth/resend-code`, { email: form.email });
      toast.success(bg ? "Нов код е изпратен" : "New code sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (success) {
    return (
      <div className="text-center py-8 space-y-4" data-testid="clinician-register-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-[#1A1A2E]">
          {bg ? "Заявката е изпратена" : "Application Submitted"}
        </h3>
        <p className="text-sm text-[#64748B] max-w-xs mx-auto">
          {bg
            ? "Вашата заявка ще бъде прегледана от администратор. Ще получите достъп след одобрение."
            : "Your application will be reviewed by an administrator. You will get access after approval."}
        </p>
        <Button variant="outline" onClick={() => navigate("/login")} data-testid="clinician-go-login-btn"
          className="mt-4 rounded-xl">
          {bg ? "Към входа" : "Go to Login"}
        </Button>
      </div>
    );
  }

  const inputCls = "h-10 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl text-sm";

  if (step === "verify") {
    return (
      <form onSubmit={handleVerify} className="space-y-4" data-testid="clinician-verify-form">
        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
          </div>
        )}
        <div className="text-center py-2">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center">
            <Check className="w-7 h-7 text-white" />
          </div>
          <p className="text-sm text-[#334155] font-medium">{bg ? "Проверете имейла си" : "Check your email"}</p>
          <p className="text-xs text-[#94A3B8] mt-1">{bg ? `Изпратихме 6-цифрен код на ${form.email}` : `We sent a 6-digit code to ${form.email}`}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-[#1A1A2E] text-sm font-medium">{bg ? "Код за потвърждение" : "Verification code"}</Label>
          <Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-14 text-center text-2xl font-bold tracking-[0.5em] bg-[#F8FAFC] border-[#E2E8F0] rounded-xl"
            data-testid="clinician-verify-code" autoFocus />
        </div>
        <Button type="submit" disabled={loading || code.length !== 6} data-testid="clinician-verify-btn"
          className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl">
          {loading ? (bg ? "Проверка..." : "Verifying...") : (bg ? "Потвърди" : "Verify")}
        </Button>
        <button type="button" onClick={handleResend} className="w-full text-xs text-[#0EA5E9] hover:underline" data-testid="clinician-resend-code-btn">
          {bg ? "Изпрати нов код" : "Resend code"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="clinician-register-form">
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="clinician-register-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Име" : "Full Name"} *</Label>
          <Input value={form.name} onChange={set("name")} required placeholder={bg ? "Д-р Иван Иванов" : "Dr. John Smith"}
            data-testid="clinician-name-input" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Специалност" : "Specialty"} *</Label>
          <Input value={form.specialty} onChange={set("specialty")} required placeholder={bg ? "Ендокринология" : "Endocrinology"}
            data-testid="clinician-specialty-input" className={inputCls} />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Имейл" : "Email"} *</Label>
        <Input type="email" value={form.email} onChange={set("email")} required placeholder="doctor@hospital.com"
          data-testid="clinician-email-input" className={inputCls} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Парола" : "Password"} *</Label>
          <Input type="password" value={form.password} onChange={set("password")} required minLength={8} placeholder="••••••••"
            data-testid="clinician-password-input" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Потвърди парола" : "Confirm Password"} *</Label>
          <Input type="password" value={form.confirmPassword} onChange={set("confirmPassword")} required minLength={8} placeholder="••••••••"
            data-testid="clinician-confirm-password-input" className={inputCls} />
        </div>
      </div>
      <PasswordChecks password={form.password} bg={bg} />

      {/* Professional Details */}
      <div className="pt-2 border-t border-[#E2E8F0]">
        <p className="text-xs font-semibold text-[#64748B] mb-2">{bg ? "Професионални данни" : "Professional Details"}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Институция" : "Institution"}</Label>
          <Input value={form.institution} onChange={set("institution")} placeholder={bg ? "Болница / Клиника" : "Hospital / Clinic"}
            data-testid="clinician-institution-input" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Институц. имейл" : "Inst. Email"}</Label>
          <Input type="email" value={form.institutional_email} onChange={set("institutional_email")} placeholder="dr@hospital.edu"
            data-testid="clinician-inst-email-input" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">ORCID</Label>
          <Input value={form.orcid} onChange={set("orcid")} placeholder="0000-0001-2345-6789"
            data-testid="clinician-orcid-input" className={inputCls} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">LinkedIn</Label>
          <Input value={form.linkedin} onChange={set("linkedin")} placeholder="linkedin.com/in/..."
            data-testid="clinician-linkedin-input" className={inputCls} />
        </div>
      </div>

      {/* File Uploads */}
      <div className="pt-2 border-t border-[#E2E8F0]">
        <p className="text-xs font-semibold text-[#64748B] mb-2">{bg ? "Документи" : "Documents"}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "CV / Автобиография" : "CV / Resume"}</Label>
          <label className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed cursor-pointer transition-colors text-xs
            ${cvFile ? "border-green-300 bg-green-50 text-green-700" : "border-[#CBD5E1] bg-[#F8FAFC] text-[#64748B] hover:border-[#0EA5E9]"}`}
            data-testid="clinician-cv-upload">
            <Upload className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{cvFile ? cvFile.name : (bg ? "Качете CV" : "Upload CV")}</span>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setCvFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div className="space-y-1">
          <Label className="text-[#1A1A2E] text-xs font-medium">{bg ? "Диплома" : "Medical Diploma"}</Label>
          <label className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed cursor-pointer transition-colors text-xs
            ${diplomaFile ? "border-green-300 bg-green-50 text-green-700" : "border-[#CBD5E1] bg-[#F8FAFC] text-[#64748B] hover:border-[#0EA5E9]"}`}
            data-testid="clinician-diploma-upload">
            <Upload className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{diplomaFile ? diplomaFile.name : (bg ? "Качете диплома" : "Upload Diploma")}</span>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setDiplomaFile(e.target.files?.[0] || null)} />
          </label>
        </div>
      </div>

      <Button type="submit" disabled={loading} data-testid="clinician-register-submit-btn"
        className="w-full h-11 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium rounded-xl shadow-lg shadow-purple-500/20 mt-2">
        {loading
          ? (bg ? "Изпращане..." : "Submitting...")
          : (bg ? "Подай заявка като лекар" : "Submit Clinician Application")}
      </Button>
    </form>
  );
}

export default function Register() {
  const { language, setLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const bg = language === "bg";
  const [mode, setMode] = useState("patient");

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4] flex flex-col">
      {/* Language Dropdown */}
      <div className="absolute top-4 right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="register-language-toggle"
              className="rounded-lg bg-white/80 backdrop-blur-sm border-[#E2E8F0] hover:bg-white text-sm font-bold">
              {language.toUpperCase()}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}
                className={`cursor-pointer ${language === lang.code ? "text-[#0EA5E9] bg-blue-50" : ""}`}>
                <span className="text-xs font-bold w-6">{lang.label}</span>
                <span className="text-sm">{lang.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Logo (wordmark вграден в PNG) */}
          <div className="text-center mb-4">
            <img src="/nutramilo-logo.png" alt="Nutramilo" className="mx-auto h-14 sm:h-16 w-auto object-contain" />
          </div>

          {/* Mode Tabs */}
          <div className="flex gap-2 mb-5 justify-center" data-testid="register-mode-tabs">
            <button onClick={() => setMode("patient")} data-testid="register-patient-tab"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all
              ${mode === "patient"
                ? "bg-[#0EA5E9] text-white shadow-lg shadow-sky-500/20"
                : "bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#0EA5E9]"}`}>
              <User className="w-4 h-4" />
              {bg ? "Пациент" : "Patient"}
            </button>
            <button onClick={() => setMode("clinician")} data-testid="register-clinician-tab"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all
              ${mode === "clinician"
                ? "bg-[#7C3AED] text-white shadow-lg shadow-purple-500/20"
                : "bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#7C3AED]"}`}>
              <Stethoscope className="w-4 h-4" />
              {bg ? "Лекар" : "Clinician"}
            </button>
          </div>

          {/* Subtitle */}
          <div className="text-center mb-5">
            {mode === "patient" ? (
              <>
                <h2 className="text-lg sm:text-xl font-bold text-[#1A1A2E] mb-1">
                  {bg ? "Започнете вашето wellness пътуване" : "Start your wellness journey"}
                </h2>
                <p className="text-sm text-[#64748B]">
                  {bg ? "Персонализирани хранителни и здравни анализи." : "Personalized nutrition and health insights."}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg sm:text-xl font-bold text-[#1A1A2E] mb-1">
                  {bg ? "Регистрация за лекари" : "Clinician Registration"}
                </h2>
                <p className="text-sm text-[#64748B]">
                  {bg ? "Достъп до клинични инструменти след верификация от администратор." : "Access clinical tools after admin verification."}
                </p>
              </>
            )}
          </div>

          {/* Form Card */}
          <Card className="card-elevated border-0">
            <CardContent className="p-5">
              {mode === "patient" ? (
                <>
                  <PatientRegisterForm bg={bg} />
                  {/* Divider + Google Login — temporarily disabled */}
                  {/*
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-[#E2E8F0]" />
                    <span className="text-xs text-[#94A3B8] font-medium">{bg ? "или" : "or"}</span>
                    <div className="flex-1 h-px bg-[#E2E8F0]" />
                  </div>
                  <GoogleLoginButton label={bg ? "Регистрация с Google" : "Sign up with Google"} />
                  */}
                </>
              ) : (
                <ClinicianRegisterForm bg={bg} />
              )}

              <div className="mt-5 text-center text-sm text-[#64748B]">
                {bg ? "Вече имате акаунт? " : "Already have an account? "}
                <Link to="/login" className="text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-testid="login-link">
                  {bg ? "Вход" : "Sign in"}
                </Link>
              </div>
            </CardContent>
          </Card>

          <p className="mt-5 text-center text-xs text-[#94A3B8] max-w-sm mx-auto leading-relaxed" data-testid="register-disclaimer">
            {bg ? "Тази платформа предоставя само информационни анализи и не замества професионален медицински съвет, диагноза или лечение."
              : language === "es" ? "Esta plataforma solo proporciona informacion y no reemplaza el consejo medico profesional, diagnostico o tratamiento."
              : "This platform provides informational insights only and does not replace professional medical advice, diagnosis, or treatment."}
          </p>
        </div>
      </div>
    </div>
  );
}
